import { HELP_GREETING, getHelpTopicsForRole, type HelpLang } from './knowledge';
import {
  actionRefsForTopic,
  buildCapabilitiesReply,
  buildHandoffOfferReply,
  detectAssistantIntent,
} from './assistantIntents';
import { actionsForRole, resolveAssistantAction } from './assistantActions';

export interface SupportHistoryItem {
  role: string;
  text: string;
}

export interface SupportAssistantAction {
  id: string;
  label: string;
  target: string;
}

export interface SupportAssistantInput {
  message: string;
  role?: string;
  lang?: HelpLang;
  history?: SupportHistoryItem[];
  ragSnippets?: Array<{ title?: string; content: string; score: number }>;
}

export interface SupportAssistantResult {
  reply: string;
  confidence: number;
  /** Offer human help — never auto-create a ticket. */
  escalate: boolean;
  category: string;
  priority: string;
  intent?: string;
  actions?: SupportAssistantAction[];
  handoffOffer?: boolean;
  followUp?: string;
  diagnosticSteps?: string[];
}

const TICKET_CREATOR_ROLES = ['banker', 'lister', 'runner'] as const;
/** Minimum hybrid/RAG score to treat a snippet as grounded evidence. */
export const RAG_EVIDENCE_MIN_SCORE = 0.08;

export function canCreateSupportTicket(role?: string): boolean {
  return Boolean(role && TICKET_CREATOR_ROLES.includes(role as typeof TICKET_CREATOR_ROLES[number]));
}

export function detectHelpLang(message: string, fallback: HelpLang = 'es'): HelpLang {
  if (/[áéíóúñ¿¡]/i.test(message)) return 'es';
  const lower = message.toLowerCase();
  const esHints = ['cómo', 'como', 'dónde', 'donde', 'quiero', 'puedo', 'cerrar', 'jugada', 'resultado', 'tope', 'ayuda', 'problema'];
  if (esHints.some((w) => lower.includes(w))) return 'es';
  const enHints = ['how', 'where', 'help', 'issue', 'problem', 'cannot', "can't", 'play', 'cap'];
  if (enHints.some((w) => lower.includes(w))) return 'en';
  return fallback;
}

function tokenize(text: string): string[] {
  return text.toLowerCase().split(/[^a-záéíóúñ0-9]+/i).filter((t) => t.length > 2);
}

function scoreTopic(message: string, topic: { keywords: string[] }): number {
  const tokens = new Set(tokenize(message));
  let score = 0;
  for (const kw of topic.keywords) {
    const parts = kw.toLowerCase().split(/\s+/);
    if (parts.length === 1) {
      if (tokens.has(parts[0]) || message.toLowerCase().includes(kw)) score += 2;
    } else if (message.toLowerCase().includes(kw)) {
      score += 3;
    }
  }
  return score;
}

function matchTopic(message: string, role?: string) {
  const candidates = getHelpTopicsForRole(role);
  let best: (typeof candidates[number]) | null = null;
  let bestScore = 0;
  for (const topic of candidates) {
    const score = scoreTopic(message, topic);
    if (score > bestScore) {
      best = topic;
      bestScore = score;
    }
  }
  return bestScore >= 2 ? best : null;
}

function summarize(text: string, max = 280): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trim()}…`;
}

function buildRagReply(
  snippet: { title?: string; content: string },
  lang: HelpLang,
): string {
  const summary = summarize(snippet.content, 260);
  if (lang === 'es') {
    return `Encontré esto en las guías${snippet.title ? ` (“${snippet.title}”)` : ''}: ${summary} Si no resuelve tu caso, dime qué paso exacto te falla y lo afinamos.`;
  }
  return `I found this in the guides${snippet.title ? ` (“${snippet.title}”)` : ''}: ${summary} If that doesn’t solve it, tell me which step fails and we’ll narrow it down.`;
}

function buildGuidedFallback(lang: HelpLang, role?: string): string {
  const roleHints: Record<string, Record<HelpLang, string>> = {
    banker: {
      en: 'As a banker, common tasks live in Overview (results, cutoffs), Plays, Users, and Config.',
      es: 'Como banquero, lo habitual está en Resumen (resultados, cierres), Jugadas, Usuarios y Config.',
    },
    lister: {
      en: 'As a lister, try New play to record bets and Results to check winners after publish.',
      es: 'Como listero, usa Nueva jugada para registrar y Resultados para ver premios tras publicar.',
    },
    runner: {
      en: 'As a runner, your route and collection stops are under Route on the overview.',
      es: 'Como corredor, tu ruta y paradas de cobro están en Ruta desde el resumen.',
    },
  };
  const hint = role ? roleHints[role]?.[lang] : '';
  if (lang === 'es') {
    return `No estoy seguro todavía. ${hint} Cuéntame con tus palabras qué quieres lograr — por ejemplo “no puedo registrar una jugada” o “¿dónde publico resultados?”.`.trim();
  }
  return `I’m not fully sure yet. ${hint} Tell me in your own words what you’re trying to do — e.g. “can’t record a play” or “where do I publish results?”.`.trim();
}

function techDiagnostics(lang: HelpLang): string[] {
  return lang === 'es'
    ? [
      'Comprueba la conexión (indicador online en la barra).',
      'Cierra y vuelve a abrir la pantalla actual.',
      'Si es acceso: verifica usuario/contraseña o MFA.',
    ]
    : [
      'Check the online indicator in the top bar.',
      'Close and reopen the current screen.',
      'For access issues: verify password or MFA.',
    ];
}

function pickNavActions(message: string, role: string | undefined, lang: HelpLang): SupportAssistantAction[] {
  const lower = message.toLowerCase();
  const ids: string[] = [];
  if (/config|tope|cap|cierre|cutoff/.test(lower)) ids.push('open_config');
  if (/jugada|play|apuesta|new\s*play|nueva/.test(lower)) ids.push('open_newplay', 'open_plays', 'open_lister_plays');
  if (/resultado|result|premio|publish/.test(lower)) ids.push('open_results', 'open_overview');
  if (/ruta|route|parada|cobro/.test(lower)) ids.push('open_route');
  if (/usuario|user|staff/.test(lower)) ids.push('open_users');
  if (/tasa|rate|cambio|exchange/.test(lower)) ids.push('open_rates');
  const out: SupportAssistantAction[] = [];
  for (const id of ids) {
    const r = resolveAssistantAction(id, role, lang);
    if (r && !out.some((a) => a.id === r.id)) out.push(r);
  }
  if (out.length === 0) return actionsForRole(role, lang).slice(0, 3);
  return out.slice(0, 3);
}

export function buildSupportReply(input: SupportAssistantInput): SupportAssistantResult {
  const message = input.message.trim();
  const lang = input.lang ?? detectHelpLang(message, 'es');
  const detected = detectAssistantIntent(message);

  if (!message || detected.intent === 'greeting') {
    return {
      reply: HELP_GREETING[lang],
      confidence: 0.9,
      escalate: false,
      category: 'other',
      priority: 'normal',
      intent: 'greeting',
    };
  }

  if (detected.intent === 'capabilities') {
    return {
      reply: buildCapabilitiesReply(lang, input.role),
      confidence: 0.95,
      escalate: false,
      category: 'other',
      priority: 'normal',
      intent: 'capabilities',
      actions: actionsForRole(input.role, lang).slice(0, 4),
    };
  }

  if (detected.intent === 'request_human' || detected.intent === 'report_issue') {
    return {
      reply: buildHandoffOfferReply(lang, message),
      confidence: 0.7,
      escalate: true,
      handoffOffer: true,
      category: detected.intent === 'report_issue' ? 'bug' : 'other',
      priority: 'normal',
      intent: detected.intent,
    };
  }

  const bestRag = input.ragSnippets
    ?.filter((s) => s.score >= RAG_EVIDENCE_MIN_SCORE)
    .sort((a, b) => b.score - a.score)[0];
  if (bestRag) {
    return {
      reply: buildRagReply(bestRag, lang),
      confidence: Math.min(0.88, 0.55 + bestRag.score),
      escalate: false,
      category: 'other',
      priority: 'normal',
      intent: 'topic',
    };
  }

  if (detected.intent === 'tech_support') {
    const steps = techDiagnostics(lang);
    return {
      reply: lang === 'es'
        ? `Probemos soporte técnico básico:\n• ${steps.join('\n• ')}\nSi sigue fallando, puedo ofrecerte un agente o un ticket.`
        : `Let’s try basic troubleshooting:\n• ${steps.join('\n• ')}\nIf it still fails, I can offer an agent or a ticket.`,
      confidence: 0.65,
      escalate: false,
      category: 'access',
      priority: 'normal',
      intent: 'tech_support',
      diagnosticSteps: steps,
      followUp: lang === 'es' ? '¿Qué error ves exactamente?' : 'What exact error do you see?',
    };
  }

  if (detected.intent === 'navigation') {
    const actions = pickNavActions(message, input.role, lang);
    return {
      reply: lang === 'es'
        ? 'Puedo llevarte a estas pantallas. Elige una o dime el nombre de la sección.'
        : 'I can take you to these screens. Pick one or tell me the section name.',
      confidence: 0.75,
      escalate: false,
      category: 'other',
      priority: 'normal',
      intent: 'navigation',
      actions,
    };
  }

  const topic = matchTopic(message, input.role);
  if (topic) {
    return {
      reply: topic[lang],
      confidence: 0.78,
      escalate: false,
      category: topic.category,
      priority: 'normal',
      intent: detected.intent === 'game_rules' ? 'game_rules' : 'role_help',
      actions: actionRefsForTopic(topic.id, input.role, lang),
    };
  }

  const vague = message.length < 12;
  if (vague || detected.intent === 'clarify') {
    return {
      reply: buildGuidedFallback(lang, input.role),
      confidence: 0.42,
      escalate: false,
      category: 'other',
      priority: 'normal',
      intent: 'clarify',
    };
  }

  return {
    reply: buildHandoffOfferReply(lang, message),
    confidence: 0.28,
    escalate: true,
    handoffOffer: true,
    category: 'other',
    priority: 'normal',
    intent: 'unknown',
  };
}
